import React from 'react';
import { X, User, Stethoscope, Calendar, Clock, MapPin, FileText, DollarSign, AlertCircle, CheckCircle, Activity, Circle } from 'lucide-react';
import { Intervention } from '../types';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

interface InterventionDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  intervention: Intervention | null;
}

const InterventionDetailModal: React.FC<InterventionDetailModalProps> = ({
  isOpen,
  onClose,
  intervention
}) => {
  if (!isOpen || !intervention) return null;

  const formatDateTime = (dateString: string) => {
    return format(new Date(dateString), "dd 'de' MMMM 'de' yyyy 'a las' HH:mm", { locale: es });
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), "dd 'de' MMMM 'de' yyyy", { locale: es });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'planned':
        return 'bg-blue-100 text-blue-800';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'planned':
        return 'Planificada';
      case 'in_progress':
        return 'En Progreso';
      case 'completed':
        return 'Completada';
      case 'cancelled':
        return 'Cancelada';
      default:
        return status;
    }
  };

  const getTypeText = (type: string) => {
    const types: { [key: string]: string } = {
      'cleaning': 'Limpieza Dental',
      'filling': 'Empaste',
      'extraction': 'Extracción',
      'root_canal': 'Endodoncia',
      'crown': 'Corona',
      'bridge': 'Puente',
      'implant': 'Implante',
      'orthodontics': 'Ortodoncia',
      'whitening': 'Blanqueamiento',
      'consultation': 'Consulta',
      'emergency': 'Emergencia',
      'other': 'Otro'
    };
    return types[type] || type;
  };

  const getAnesthesiaText = (anesthesia: string) => {
    const types: { [key: string]: string } = {
      'none': 'Sin anestesia',
      'local': 'Anestesia local',
      'general': 'Anestesia general',
      'sedation': 'Sedación'
    };
    return types[anesthesia] || anesthesia;
  };

  const patientName = typeof intervention.patient === 'string' 
    ? intervention.patient 
    : `${intervention.patient?.firstName} ${intervention.patient?.lastName}`;

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Stethoscope className="h-6 w-6 text-dental-600" />
            <h3 className="text-xl font-semibold text-gray-900">
              Detalles de la Intervención
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Status and Type */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(intervention.status)}`}>
                {getStatusText(intervention.status)}
              </span>
              <div className="flex items-center space-x-2">
                <Activity className="h-4 w-4 text-gray-400" />
                <span className="text-lg font-medium text-gray-900">{getTypeText(intervention.type)}</span>
              </div>
            </div>
            {intervention.cost && (
              <div className="flex items-center space-x-1 text-green-600">
                <DollarSign className="h-5 w-5" />
                <span className="text-lg font-semibold">{intervention.cost}€</span>
              </div>
            )}
          </div>

          {/* Main Information Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-6">
              {/* Patient Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                  <User className="h-4 w-4 mr-2" />
                  Información del Paciente
                </h4>
                <div className="space-y-2">
                  <p className="text-sm">
                    <span className="font-medium">Paciente:</span> {patientName}
                  </p>
                  {typeof intervention.patient === 'object' && intervention.patient?.email && (
                    <p className="text-sm">
                      <span className="font-medium">Email:</span> {intervention.patient.email}
                    </p>
                  )}
                  {typeof intervention.patient === 'object' && intervention.patient?.phone && (
                    <p className="text-sm">
                      <span className="font-medium">Teléfono:</span> {intervention.patient.phone}
                    </p>
                  )}
                </div>
              </div>

              {/* Procedure Information */}
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <FileText className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Procedimiento</p>
                    <p className="text-sm text-gray-600">{intervention.procedure}</p>
                  </div>
                </div>

                {intervention.description && (
                  <div className="flex items-start space-x-3">
                    <FileText className="h-5 w-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Descripción</p>
                      <p className="text-sm text-gray-600 whitespace-pre-wrap">{intervention.description}</p>
                    </div>
                  </div>
                )}

                {/* Tooth Information */}
                {intervention.tooth && (
                  <div className="flex items-start space-x-3">
                    <Circle className="h-5 w-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Información del Diente</p>
                      <div className="text-sm text-gray-600 space-y-1">
                        {intervention.tooth.number && (
                          <p><span className="font-medium">Número:</span> {intervention.tooth.number}</p>
                        )}
                        {intervention.tooth.surface && (
                          <p><span className="font-medium">Superficie:</span> {intervention.tooth.surface}</p>
                        )}
                        {intervention.tooth.quadrant && (
                          <p><span className="font-medium">Cuadrante:</span> {intervention.tooth.quadrant}</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              {/* Professional Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                  <User className="h-4 w-4 mr-2" />
                  Equipo Médico
                </h4>
                <div className="space-y-2">
                  <p className="text-sm">
                    <span className="font-medium">Dentista:</span> {intervention.dentist}
                  </p>
                  {intervention.assistant && (
                    <p className="text-sm">
                      <span className="font-medium">Asistente:</span> {intervention.assistant}
                    </p>
                  )}
                </div>
              </div>

              {/* Technical Details */}
              <div className="space-y-4">
                {intervention.duration && (
                  <div className="flex items-start space-x-3">
                    <Clock className="h-5 w-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Duración</p>
                      <p className="text-sm text-gray-600">{intervention.duration} minutos</p>
                    </div>
                  </div>
                )}

                {intervention.anesthesia && (
                  <div className="flex items-start space-x-3">
                    <Activity className="h-5 w-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Anestesia</p>
                      <p className="text-sm text-gray-600">{getAnesthesiaText(intervention.anesthesia)}</p>
                    </div>
                  </div>
                )}

                {intervention.materials && intervention.materials.length > 0 && (
                  <div className="flex items-start space-x-3">
                    <FileText className="h-5 w-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Materiales Utilizados</p>
                      <ul className="text-sm text-gray-600 list-disc list-inside">
                        {intervention.materials.map((material, index) => (
                          <li key={index}>{material}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Complications and Notes */}
          {(intervention.complications || intervention.notes) && (
            <div className="border-t border-gray-200 pt-6 space-y-4">
              {intervention.complications && (
                <div className="bg-red-50 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <AlertCircle className="h-5 w-5 text-red-400 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-red-900">Complicaciones</p>
                      <p className="text-sm text-red-700 mt-1 whitespace-pre-wrap">{intervention.complications}</p>
                    </div>
                  </div>
                </div>
              )}

              {intervention.notes && (
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <FileText className="h-5 w-5 text-blue-400 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-blue-900">Notas Adicionales</p>
                      <p className="text-sm text-blue-700 mt-1 whitespace-pre-wrap">{intervention.notes}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Follow-up Information */}
          {intervention.followUpRequired && (
            <div className="border-t border-gray-200 pt-6">
              <div className="bg-yellow-50 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Calendar className="h-5 w-5 text-yellow-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-yellow-900">Seguimiento Requerido</p>
                    {intervention.followUpDate && (
                      <p className="text-sm text-yellow-700 mt-1">
                        Fecha programada: {formatDate(intervention.followUpDate)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Timeline */}
          <div className="border-t border-gray-200 pt-6">
            <h4 className="text-sm font-semibold text-gray-900 mb-4">Historial</h4>
            <div className="space-y-2 text-xs text-gray-500">
              <div className="flex justify-between">
                <span className="font-medium">Creada:</span>
                <span>{formatDateTime(intervention.createdAt)}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">Última actualización:</span>
                <span>{formatDateTime(intervention.updatedAt)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dental-500"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default InterventionDetailModal;